/**
 * The city seen through the lobby's windows, from high up at blue hour: a painted panorama
 * (sky, haze, a far skyline and the lights of the city below), tall tapered towers with lit
 * windows at mid distance, glowing crowns on their tops, a carpet of street light far below
 * and a few vehicles flying slow lanes between the towers. Six draw calls; the vehicles'
 * transforms are the only per-frame work.
 */
import * as THREE from 'three';
import { glowMaterial, radialTexture, seeded } from './glow.ts';

export interface City {
  group: THREE.Group;
  update(dt: number): void;
}

const DEG = Math.PI / 180;

export function buildCity(): City {
  const group = new THREE.Group();
  group.name = 'city';
  const rand = seeded(2077);

  // Panorama.
  const panorama = new THREE.Mesh(
    new THREE.CylinderGeometry(240, 240, 320, 96, 1, true),
    new THREE.MeshBasicMaterial({
      map: paintPanorama(),
      side: THREE.BackSide,
      fog: false,
      toneMapped: false,
    }),
  );
  // Drawn in the normal opaque order (farthest last), so the hall's walls hide it before it
  // is shaded; drawn first it filled the whole screen every frame.
  group.add(panorama);

  // Towers at mid distance, mostly where the windows look.
  const towers: { x: number; z: number; w: number; top: number; round: boolean; tint: number }[] =
    [];
  const arcs: [number, number][] = [
    [50, 160],
    [200, 310],
  ];
  while (towers.length < 54) {
    const [a, b] = arcs[towers.length % 2]!;
    const phi = (a + rand() * (b - a)) * DEG;
    const r = 75 + rand() * 110;
    const near = 1 - (r - 75) / 110;
    towers.push({
      x: Math.sin(phi) * r,
      z: -Math.cos(phi) * r,
      w: 2.6 + rand() * 3.8,
      top: -20 + rand() * 70 + near * 30,
      round: rand() < 0.35,
      tint: rand(),
    });
  }
  const base = -140;
  const facade = paintFacade();
  const towerMaterial = new THREE.MeshBasicMaterial({ map: facade, color: '#ffffff' });
  const prism = new THREE.CylinderGeometry(0.62, 1, 1, 6, 1).translate(0, 0.5, 0);
  const round = new THREE.CylinderGeometry(0.7, 0.92, 1, 20, 1).translate(0, 0.5, 0);
  const hex = new THREE.InstancedMesh(prism, towerMaterial, towers.filter((t) => !t.round).length);
  const cyl = new THREE.InstancedMesh(round, towerMaterial, towers.filter((t) => t.round).length);
  const crowns = new THREE.InstancedMesh(
    new THREE.SphereGeometry(1, 12, 8),
    glowMaterial('#ffffff', 1),
    towers.length,
  );
  const d = new THREE.Object3D();
  const c = new THREE.Color();
  let hi = 0;
  let ci = 0;
  towers.forEach((t, i) => {
    const h = t.top - base;
    d.position.set(t.x, base, t.z);
    d.rotation.set(0, t.tint * Math.PI, 0);
    d.scale.set(t.w, h, t.w);
    d.updateMatrix();
    c.setHSL(0.58 + t.tint * 0.06, 0.35, 0.55 + t.tint * 0.25);
    if (t.round) {
      cyl.setMatrixAt(ci, d.matrix);
      cyl.setColorAt(ci++, c);
    } else {
      hex.setMatrixAt(hi, d.matrix);
      hex.setColorAt(hi++, c);
    }
    d.position.set(t.x, t.top + 0.4, t.z);
    d.rotation.set(0, 0, 0);
    d.scale.setScalar(0.35 + t.w * 0.05);
    d.updateMatrix();
    crowns.setMatrixAt(i, d.matrix);
    crowns.setColorAt(i, c.set(t.tint < 0.55 ? '#7fe3ff' : t.tint < 0.85 ? '#ffffff' : '#ffb35c'));
  });
  group.add(hex, cyl, crowns);

  // The city's lights far below are painted into the panorama's lower half (a ground plane
  // under it was one more full layer of fill for little depth).

  // Flying vehicles on slow elliptical lanes, each with a soft light trail.
  const vehicles = Array.from({ length: 14 }, (_, i) => ({
    rx: 60 + rand() * 70,
    rz: 55 + rand() * 75,
    y: 6 + rand() * 40,
    speed: (0.03 + rand() * 0.035) * (i % 2 ? 1 : -1),
    phase: rand() * Math.PI * 2,
    bob: rand() * Math.PI * 2,
  }));
  const bodies = new THREE.InstancedMesh(
    new THREE.CapsuleGeometry(0.45, 2.4, 4, 8).rotateZ(Math.PI / 2),
    glowMaterial('#dff6ff', 1),
    vehicles.length,
  );
  const trails = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(1, 1),
    glowMaterial('#3fd6ff', 0.75, radialTexture()),
    vehicles.length,
  );
  bodies.frustumCulled = false;
  trails.frustumCulled = false;
  group.add(bodies, trails);
  const pos = new THREE.Vector3();
  const ahead = new THREE.Vector3();
  let t = 0;

  return {
    group,
    update: (dt) => {
      t += dt;
      vehicles.forEach((v, i) => {
        const a = v.phase + t * v.speed;
        pos.set(Math.cos(a) * v.rx, v.y + Math.sin(t * 0.4 + v.bob) * 1.5, Math.sin(a) * v.rz);
        ahead.set(
          Math.cos(a + 0.01 * Math.sign(v.speed)) * v.rx,
          pos.y,
          Math.sin(a + 0.01 * Math.sign(v.speed)) * v.rz,
        );
        d.position.copy(pos);
        d.lookAt(ahead);
        d.rotateY(Math.PI / 2);
        d.scale.setScalar(1);
        d.updateMatrix();
        bodies.setMatrixAt(i, d.matrix);
        // Trail: a stretched glow behind the vehicle, facing up so it reads from above and side.
        d.translateX(-5);
        d.scale.set(10, 1.6, 1);
        d.updateMatrix();
        trails.setMatrixAt(i, d.matrix);
      });
      bodies.instanceMatrix.needsUpdate = true;
      trails.instanceMatrix.needsUpdate = true;
    },
  };
}

/** Sky to horizon haze, a far skyline, and the city's lights below the horizon. */
function paintPanorama(): THREE.CanvasTexture {
  const W = 2048;
  const H = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;
  const horizon = H / 2;
  const sky = g.createLinearGradient(0, 0, 0, horizon);
  sky.addColorStop(0, '#03060c');
  sky.addColorStop(0.45, '#0a1730');
  sky.addColorStop(0.8, '#1d3c63');
  sky.addColorStop(1, '#4d7fa8');
  g.fillStyle = sky;
  g.fillRect(0, 0, W, horizon);
  // A few stars high up.
  const r = seeded(91);
  g.fillStyle = 'rgba(220,235,255,0.7)';
  for (let i = 0; i < 160; i++) g.fillRect(r() * W, r() * horizon * 0.45, 1, 1);
  // Thin high clouds catching the last light.
  for (let i = 0; i < 9; i++) {
    const y = horizon * (0.45 + r() * 0.3);
    const grad = g.createLinearGradient(0, y - 6, 0, y + 6);
    grad.addColorStop(0, 'rgba(120,170,215,0)');
    grad.addColorStop(0.5, `rgba(140,190,230,${(0.08 + r() * 0.1).toFixed(2)})`);
    grad.addColorStop(1, 'rgba(120,170,215,0)');
    g.fillStyle = grad;
    const x = r() * W;
    g.fillRect(x, y - 6, 300 + r() * 500, 12);
  }
  // Far skyline, periodic across the width.
  const pxPerM = H / 320;
  let x = 0;
  while (x < W) {
    const bw = 6 + r() * 22;
    const bh = (10 + r() * 34) * pxPerM * (r() < 0.12 ? 2.2 : 1);
    g.fillStyle = `rgb(${18 + r() * 10},${30 + r() * 12},${50 + r() * 16})`;
    g.fillRect(x, horizon - bh, bw, bh);
    g.fillStyle = 'rgba(170,215,255,0.35)';
    for (let wy = horizon - bh + 3; wy < horizon - 2; wy += 4)
      for (let wx = x + 1; wx < x + bw - 1; wx += 3) if (r() < 0.18) g.fillRect(wx, wy, 1, 1);
    if (r() < 0.2) {
      g.fillStyle = 'rgba(127,227,255,0.9)';
      g.fillRect(x + bw / 2 - 1, horizon - bh - 2, 2, 2);
    }
    x += bw + r() * 4;
  }
  // Haze over the horizon.
  const haze = g.createLinearGradient(0, horizon - 60, 0, horizon + 40);
  haze.addColorStop(0, 'rgba(90,150,200,0)');
  haze.addColorStop(0.6, 'rgba(90,160,210,0.35)');
  haze.addColorStop(1, 'rgba(20,40,70,0)');
  g.fillStyle = haze;
  g.fillRect(0, horizon - 60, W, 100);
  // The city below: dark, with lights denser toward the horizon.
  const below = g.createLinearGradient(0, horizon, 0, H);
  below.addColorStop(0, '#0d1a2c');
  below.addColorStop(1, '#03060a');
  g.fillStyle = below;
  g.fillRect(0, horizon, W, H - horizon);
  for (let i = 0; i < 5200; i++) {
    const depth = Math.pow(r(), 2.4);
    const y = horizon + 2 + depth * (H - horizon - 2);
    const warm = r() < 0.45;
    const a = 0.25 + (1 - depth) * 0.55;
    g.fillStyle = warm ? `rgba(255,190,120,${a.toFixed(2)})` : `rgba(160,220,255,${a.toFixed(2)})`;
    g.fillRect(r() * W, y, 1 + depth * 2, 1);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.set(2, 1);
  texture.anisotropy = 4;
  return texture;
}

/** A tower facade: dark glass with rows of lit windows and a few vertical light seams. */
function paintFacade(): THREE.CanvasTexture {
  const W = 64;
  const H = 512;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;
  g.fillStyle = '#0d1624';
  g.fillRect(0, 0, W, H);
  const r = seeded(5);
  for (let y = 2; y < H; y += 5)
    for (let x = 1; x < W; x += 4) {
      const lit = r();
      if (lit < 0.2) {
        g.fillStyle = lit < 0.05 ? 'rgba(255,214,160,0.75)' : 'rgba(170,215,255,0.5)';
        g.fillRect(x, y, 2, 1);
      }
    }
  g.fillStyle = 'rgba(127,227,255,0.55)';
  g.fillRect(W / 2, 0, 1, H);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2, 3);
  return texture;
}
